import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';

function AgriEngagement({ filters, loading, setLoading, isStaging, onDrilldown }) {
  const [convByRegion, setConvByRegion] = useState({ labels: [], data: [] });
  const [teamUrgency, setTeamUrgency] = useState({ labels: [], data: [] });
  const [teamIntent, setTeamIntent] = useState({ labels: [], data: [] });
  const [qualityByRegion, setQualityByRegion] = useState({ labels: [], datasets: [] });
  const [agentScorecard, setAgentScorecard] = useState([]);
  const [agentLeaderboard, setAgentLeaderboard] = useState([]);
  const [agentPerfTrend, setAgentPerfTrend] = useState({ labels: [], datasets: [] });
  const [fieldLeaders, setFieldLeaders] = useState([]);
  const [sentimentByEntity, setSentimentByEntity] = useState({ labels: [], datasets: [] });
  const [topicDistribution, setTopicDistribution] = useState({ labels: [], data: [] });
  const [trainingNeeds, setTrainingNeeds] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });

    Promise.all([
      axios.get(`/api/agri/engagement/conv-by-region?${params}`),
      axios.get(`/api/agri/engagement/team-urgency?${params}`),
      axios.get(`/api/agri/engagement/team-intent?${params}`),
      axios.get(`/api/agri/engagement/quality-by-region?${params}`),
      axios.get(`/api/agri/engagement/agent-scorecard?${params}`),
      axios.get(`/api/agri/engagement/agent-leaderboard?${params}`),
      axios.get(`/api/agri/engagement/agent-perf-trend?${params}`),
      axios.get(`/api/agri/engagement/field-leaders?${params}`),
      axios.get(`/api/agri/engagement/sentiment-by-entity?${params}`),
      axios.get(`/api/agri/engagement/topic-distribution?${params}`),
      axios.get(`/api/agri/engagement/training-needs?${params}`),
    ])
      .then(([crRes, tuRes, tiRes, qrRes, asRes, alRes, apRes, flRes, seRes, tdRes, tnRes]) => {
        setConvByRegion(crRes.data);
        setTeamUrgency(tuRes.data);
        setTeamIntent(tiRes.data);
        setQualityByRegion(qrRes.data);
        setAgentScorecard(asRes.data || []);
        setAgentLeaderboard(alRes.data || []);
        setAgentPerfTrend(apRes.data);
        setFieldLeaders(flRes.data || []);
        setSentimentByEntity(seRes.data);
        setTopicDistribution(tdRes.data);
        setTrainingNeeds(tnRes.data || []);
      })
      .catch(err => console.error('Error loading Agri Engagement:', err))
      .finally(() => setLoading(false));
  }, [filters, setLoading]);

  const cardStyle = {
    background: '#fff', borderRadius: 8, padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };
  const gridStyle = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: 16,
  };
  const plotLayout = { margin: { t: 30, b: 40, l: 50, r: 20 }, height: 300, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent' };
  const colors = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
  const sentimentColors = { Positive: '#2ecc71', Neutral: '#f39c12', Negative: '#e74c3c' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
  const thStyle = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #ddd', fontWeight: 600 };
  const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #eee' };

  return (
    <div style={gridStyle}>
      {/* Conversations by Region — pie chart */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Conversations by Region</h3>
        {(convByRegion.labels || []).length > 0 ? (
          <Plot
            data={[{
              labels: convByRegion.labels,
              values: convByRegion.data,
              type: 'pie',
              hole: 0.4,
              marker: { colors },
            }]}
            layout={{ ...plotLayout, showlegend: true }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Team Urgency — single bar chart (flat labels/data) */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Team Urgency</h3>
        {(teamUrgency.labels || []).length > 0 ? (
          <Plot
            data={[{
              x: teamUrgency.labels,
              y: teamUrgency.data,
              type: 'bar',
              marker: {
                color: teamUrgency.labels.map(l =>
                  l === 'critical' ? '#e74c3c' : l === 'high' ? '#f39c12' : l === 'medium' ? '#3498db' : '#2ecc71'
                ),
              },
            }]}
            layout={{ ...plotLayout, xaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Team Intent — single bar chart (flat labels/data) */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Team Intent</h3>
        {(teamIntent.labels || []).length > 0 ? (
          <Plot
            data={[{
              x: teamIntent.labels,
              y: teamIntent.data,
              type: 'bar',
              marker: { color: '#9b59b6' },
            }]}
            layout={{ ...plotLayout, xaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Quality by Region — grouped bar (positive/neutral/negative per region) */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Quality by Region</h3>
        {(qualityByRegion.labels || []).length > 0 ? (
          <Plot
            data={(qualityByRegion.datasets || []).map((ds) => ({
              x: qualityByRegion.labels,
              y: ds.data,
              type: 'bar',
              name: ds.label,
              marker: { color: sentimentColors[ds.label] || '#999' },
            }))}
            layout={{ ...plotLayout, barmode: 'stack', xaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Agent Scorecard — table */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Agent Scorecard</h3>
        {agentScorecard.length > 0 ? (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Agent</th>
                  <th style={thStyle}>Conversations</th>
                  <th style={thStyle}>Avg Sentiment</th>
                  <th style={thStyle}>Urgent Handled</th>
                </tr>
              </thead>
              <tbody>
                {agentScorecard.map((a, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{a.agent_name}</td>
                    <td style={tdStyle}>{a.total_convs}</td>
                    <td style={tdStyle}>{a.avg_sentiment != null ? a.avg_sentiment : 'N/A'}</td>
                    <td style={tdStyle}>{a.urgent_handled}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Agent Leaderboard — table */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Agent Leaderboard</h3>
        {agentLeaderboard.length > 0 ? (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Rank</th>
                  <th style={thStyle}>Agent</th>
                  <th style={thStyle}>Conversations</th>
                  <th style={thStyle}>Score</th>
                </tr>
              </thead>
              <tbody>
                {agentLeaderboard.map((a, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={tdStyle}>{a.agent_name}</td>
                    <td style={tdStyle}>{a.conversations}</td>
                    <td style={tdStyle}>{a.performance_score != null ? a.performance_score : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Agent Performance Trend — multi-series line */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Agent Performance Trend</h3>
        <Plot
          data={(agentPerfTrend.datasets || []).map((ds, i) => ({
            x: agentPerfTrend.labels,
            y: ds.data,
            type: 'scatter',
            mode: 'lines+markers',
            name: ds.label,
            line: { color: colors[i % colors.length] },
          }))}
          layout={plotLayout}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
        />
      </div>

      {/* Field Leaders — bubble chart */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Field Leaders</h3>
        {fieldLeaders.length > 0 ? (
          <Plot
            data={[{
              x: fieldLeaders.map(l => l.x),
              y: fieldLeaders.map(l => l.y),
              text: fieldLeaders.map(l => l.name),
              mode: 'markers+text',
              type: 'scatter',
              textposition: 'top center',
              marker: {
                size: fieldLeaders.map(l => Math.max(15, Math.sqrt(l.r || 10) * 4)),
                color: fieldLeaders.map((_, i) => colors[i % colors.length]),
                opacity: 0.7,
              },
            }]}
            layout={{ ...plotLayout, xaxis: { title: 'Conversations' }, yaxis: { title: 'Avg Sentiment' } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Sentiment by Entity — grouped bar */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Sentiment by Entity</h3>
        {(sentimentByEntity.labels || []).length > 0 ? (
          <Plot
            data={(sentimentByEntity.datasets || []).map((ds) => ({
              x: sentimentByEntity.labels,
              y: ds.data,
              type: 'bar',
              name: ds.label,
              marker: { color: sentimentColors[ds.label] || '#999' },
            }))}
            layout={{ ...plotLayout, barmode: 'group' }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Topic Distribution — pie chart */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Topic Distribution</h3>
        {(topicDistribution.labels || []).length > 0 ? (
          <Plot
            data={[{
              labels: topicDistribution.labels,
              values: topicDistribution.data,
              type: 'pie',
              hole: 0.4,
              marker: { colors },
            }]}
            layout={{ ...plotLayout, showlegend: true }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Training Needs — table */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Training Needs</h3>
        {trainingNeeds.length > 0 ? (
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Agent</th>
                  <th style={thStyle}>Weak Area</th>
                  <th style={thStyle}>Negative Count</th>
                  <th style={thStyle}>Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {trainingNeeds.map((t, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{t.agent_name}</td>
                    <td style={tdStyle}>{t.weak_area}</td>
                    <td style={tdStyle}>{t.negative_count}</td>
                    <td style={tdStyle}>{t.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No training data</div>
        )}
      </div>
    </div>
  );
}

export default AgriEngagement;
